/*****************************************************************
 *  CUSTOMER BUTLER - THE CUSTOMER LIST (clean look)               *
 *  File: apps/web/src/pages/customer/CustomerListPage.js          *
 *  Address: /app/customers                                        *
 *  What it does: Zoho's Contacts list - Zoho's saved views and    *
 *  columns - in the Butler look, with search, sort and paging,    *
 *  and an "Add customer" sheet that works with no signal (it      *
 *  saves on the device and syncs later, as the old page did).     *
 *  Reads: the layout door, the customer list door (or, with no    *
 *  signal, the device copy).  Writes: a new customer (Add only).  *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE ADD CUSTOMER SHEET                             *
 *  SECTION 2 - THE PAGE                                           *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './customerIcons';
import { PageFrame, QuietLine, Sheet, Notice } from './customerParts';
import { RecordList } from './RecordList';
import { useCustomerAccessView } from './customerAccessView';
import { fetchLayout, fetchCustomerPage } from './customerApi';
import { loadCustomers, createCustomerOffline } from '../../offline/crm';

/*****************************************************************
 *  SECTION 1 - THE ADD CUSTOMER SHEET                             *
 *  Only the four facts a person usually has on the phone; the     *
 *  rest of Zoho's fields are filled in on the record itself.      *
 *****************************************************************/

const NEW_CUSTOMER_BOXES = [
  { key: 'firstName', label: 'First Name', type: 'text' },
  { key: 'lastName', label: 'Last Name', type: 'text' },
  { key: 'primaryPhone', label: 'Phone', type: 'tel' },
  { key: 'primaryEmail', label: 'Email', type: 'email' },
];

/* ---- AddCustomerSheet({ onClose, onSaved }) -------------------- */
export function AddCustomerSheet({ onClose, onSaved }) {
  const [typedValues, setTypedValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [problemWords, setProblemWords] = useState('');

  function typeInto(boxKey, boxValue) {
    setTypedValues((valuesNow) => ({ ...valuesNow, [boxKey]: boxValue }));
  }

  async function saveCustomer(submitEvent) {
    submitEvent.preventDefault();

    const hasAName = Boolean((typedValues.firstName || '').trim() || (typedValues.lastName || '').trim());

    if (!hasAName) {
      setProblemWords('Type a first or last name first.');
      return;
    }

    setIsSaving(true);
    setProblemWords('');

    try {
      await createCustomerOffline({ type: 'person', status: 'active', ...typedValues, customFields: {} });
      onSaved();
    } catch (saveError) {
      setIsSaving(false);
      setProblemWords('The customer did not save. Nothing was added.');
    }
  }

  return (
    <Sheet title="Add customer" onClose={onClose}>
      <form onSubmit={saveCustomer}>
        {NEW_CUSTOMER_BOXES.map((oneBox) => (
          <div key={oneBox.key}>
            <label className="cb-label" htmlFor={`cb-new-${oneBox.key}`}>{oneBox.label}</label>
            <input
              id={`cb-new-${oneBox.key}`}
              className="cb-input"
              type={oneBox.type}
              value={typedValues[oneBox.key] || ''}
              onChange={(changeEvent) => typeInto(oneBox.key, changeEvent.target.value)}
            />
          </div>
        ))}
        {problemWords ? <p className="cb-edit-error" role="alert">{problemWords}</p> : null}
        <div className="cb-sheet-acts">
          <button type="button" className="bc-btn is-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="bc-btn is-primary" disabled={isSaving}>
            {isSaving ? 'Saving' : 'Add customer'}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

/*****************************************************************
 *  SECTION 2 - THE PAGE                                           *
 *****************************************************************/

/* ---- readOfflineCustomers(search) -- the device copy, searched ---- */
export async function readOfflineCustomers(search) {
  const offlineAnswer = await loadCustomers({ search });

  return offlineAnswer.rows || [];
}

/* ---- CustomerListPage({ onChooseSkin }) ------------------------ */
export function CustomerListPage({ onChooseSkin }) {
  const [layout, setLayout] = useState(null);
  const [layoutProblem, setLayoutProblem] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [listVersion, setListVersion] = useState(0);    // bumps to reload the list after an add
  const [savedWords, setSavedWords] = useState('');
  const accessView = useCustomerAccessView();          // Look up = no Add (Honest UI, 2026-10-14)

  useEffect(() => {
    fetchLayout('Customer')
      .then(setLayout)
      .catch(() => setLayoutProblem('The customer list could not be loaded. Check the connection and try again.'));
  }, []);

  // A fresh function each time the list must reload, so RecordList asks again.
  // (listVersion is read inside so the hook rule sees why it is a dependency.)
  const fetchPage = useCallback((listChoice) => {
    const askedAfterAdd = listVersion;

    return fetchCustomerPage({ ...listChoice, askedAfterAdd });
  }, [listVersion]);

  function finishAdding() {
    setIsAdding(false);
    setSavedWords('Customer added. Open it to fill in the rest of the fields.');
    setListVersion((versionNow) => versionNow + 1);
  }

  return (
    <PageFrame onChooseSkin={onChooseSkin}>
      <header className="cb-head">
        <div>
          <h1 className="cb-title">Customers</h1>
          <p className="cb-sub">Zoho's contact fields, sections and views.</p>
        </div>
        {accessView.mayWrite ? (
          <button type="button" className="bc-btn is-primary" onClick={() => setIsAdding(true)}>
            <Icon name="plus" /> Add customer
          </button>
        ) : null}
      </header>

      {savedWords ? <Notice tone="ok" icon="check">{savedWords}</Notice> : null}
      {layoutProblem ? <Notice tone="bad" icon="warn">{layoutProblem}</Notice> : null}
      {!layout && !layoutProblem ? <QuietLine icon="clock">Loading</QuietLine> : null}

      {layout ? (
        <RecordList
          layout={layout}
          fetchPage={fetchPage}
          fetchOffline={readOfflineCustomers}
          linkFor={(row) => `/customers/${row.id}`}
          nameFallback={(row) => row.displayName || 'Unnamed customer'}
          searchHint="Search by name, email, phone or company"
          nounOne="customer"
          nounMany="customers"
        />
      ) : null}

      {isAdding && accessView.mayWrite ? <AddCustomerSheet onClose={() => setIsAdding(false)} onSaved={finishAdding} /> : null}
    </PageFrame>
  );
}

export default CustomerListPage;
